import type { LucideIcon } from 'lucide-react';
import { Users, MapPin, BookOpen, Route, Film, File, Clock, FileText, NotepadText, Tags, Share2, History, FileStack, Sparkles, Settings, Trash2, Download, BarChart3, Library, Globe, Layers } from 'lucide-react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { EntityType } from './entity-utils';
import { ShareUser } from './share-user-icon';

export const CONCEPT_ICONS = {
  character: Users,
  location: MapPin,
  manual_chapter: BookOpen,
  storyline: Route,
  scene: Film,
  resource: File,
  timeline_item: Clock,
  document: FileText,
  note: NotepadText,
  tag: Tags,
  share: Share2,
  user: ShareUser,
  version_history: History,
} as const satisfies Record<string, LucideIcon>;

export const PROJECT_SECTION_ICONS = {
  documents: FileStack,
  documentation: Library,
  worldbuilding: Globe,
  structure: Layers,
  resources: CONCEPT_ICONS.resource,
  timeline: CONCEPT_ICONS.timeline_item,
  notes: CONCEPT_ICONS.note,
  tags: CONCEPT_ICONS.tag,
  characters: CONCEPT_ICONS.character,
  locations: CONCEPT_ICONS.location,
  storylines: CONCEPT_ICONS.storyline,
  scenes: CONCEPT_ICONS.scene,
  chapters: CONCEPT_ICONS.manual_chapter,
  analysis: Sparkles,
  settings: Settings,
  share: CONCEPT_ICONS.share,
  trash: Trash2,
} as const satisfies Record<string, LucideIcon>;

export type ProjectNavSectionId = keyof typeof PROJECT_SECTION_ICONS;

export function getProjectSectionIcon(sectionId: string): LucideIcon | undefined {
  if (sectionId in PROJECT_SECTION_ICONS) {
    return PROJECT_SECTION_ICONS[sectionId as ProjectNavSectionId];
  }
  return undefined;
}

export const DOCUMENT_SECTION_ICONS = {
  technicalSheet: FileText,
  export: Download,
  share: CONCEPT_ICONS.share,
  versionHistory: CONCEPT_ICONS.version_history,
  stats: BarChart3,
  settings: Settings,
} as const satisfies Record<string, LucideIcon>;

export const DOCUMENT_SECTION_LABEL_KEYS = {
  technicalSheet: 'technical_sheet',
  export: 'export_document',
  share: 'share_document',
  versionHistory: 'versionHistory.title',
  stats: 'documentStats.title',
  settings: 'document_settings',
} as const satisfies Record<string, string>;

export type DocumentSectionId = keyof typeof DOCUMENT_SECTION_ICONS;

export function getDocumentSectionIcon(sectionId: string): LucideIcon | undefined {
  if (sectionId in DOCUMENT_SECTION_ICONS) {
    return DOCUMENT_SECTION_ICONS[sectionId as DocumentSectionId];
  }
  return undefined;
}

export function getDocumentSectionLabelKey(sectionId: string): string | undefined {
  if (sectionId in DOCUMENT_SECTION_LABEL_KEYS) {
    return DOCUMENT_SECTION_LABEL_KEYS[sectionId as DocumentSectionId];
  }
  return undefined;
}

export type ConceptId = keyof typeof CONCEPT_ICONS;

export const ENTITY_TYPE_LUCIDE_ICONS: Record<EntityType, LucideIcon> = {
  character: CONCEPT_ICONS.character,
  location: CONCEPT_ICONS.location,
  manual_chapter: CONCEPT_ICONS.manual_chapter,
  storyline: CONCEPT_ICONS.storyline,
  scene: CONCEPT_ICONS.scene,
  resource: CONCEPT_ICONS.resource,
  timeline_item: CONCEPT_ICONS.timeline_item,
  document: CONCEPT_ICONS.document,
};

export function getEntityIconSvg(type: string): string {
  const Icon =
    type === 'user'
      ? CONCEPT_ICONS.user
      : ENTITY_TYPE_LUCIDE_ICONS[type as EntityType] ?? CONCEPT_ICONS.document;
  return renderToStaticMarkup(
    <Icon width={14} height={14} strokeWidth={2} className="lucide" aria-hidden />
  );
}
